import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TIMES = [
  "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM",
  "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM",
];

export default function Contact() {
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [referral, setReferral] = useState({ status: "idle", message: "", discount: 0 });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    time: "",
    location: "",
    message: "",
    referred_by: "",
  });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateReferral = async () => {
    const code = (form.referred_by || "").trim();
    if (!code) {
      setReferral({ status: "idle", message: "", discount: 0 });
      return;
    }
    setReferral({ status: "checking", message: "Checking…", discount: 0 });
    try {
      const { data } = await api.get(`/referrals/validate?code=${encodeURIComponent(code)}`);
      if (data.valid) {
        setReferral({ status: "valid", message: data.message, discount: data.discount });
      } else {
        setReferral({ status: "invalid", message: data.message, discount: 0 });
      }
    } catch {
      setReferral({ status: "invalid", message: "Couldn't verify code", discount: 0 });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !date || !form.time) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        appointment_date: format(date, "yyyy-MM-dd"),
        appointment_time: form.time,
        location: form.location,
        message: form.message,
        referred_by: form.referred_by || null,
      });
      toast.success("Inquiry received — taking you to secure the date with a $50 deposit…");

      // Create Stripe checkout session and redirect
      const { data: checkout } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-melissa-bg">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-overline text-melissa-rose mb-6">
            05 — Reserve a Date
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-melissa-ink leading-tight tracking-tight">
            Tell me about the <em className="italic text-melissa-rose">moment.</em>
          </h2>
          <p className="mt-8 text-base font-light text-melissa-inkSoft leading-relaxed max-w-md">
            Share a few details and a date that's on your heart. I respond personally
            within 48 hours, usually with a question or two before booking.
          </p>
          <div className="mt-12 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft mb-2">Studio</div>
              <div className="font-serif text-xl text-melissa-ink">By appointment · NYC & travel</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft mb-2">Email</div>
              <a
                href="mailto:melissaruthodonnell@gmail.com"
                data-testid="contact-email-link"
                className="font-serif text-xl text-melissa-ink hover:text-melissa-rose"
              >
                melissaruthodonnell@gmail.com
              </a>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft mb-2">Instagram</div>
              <div className="font-serif text-xl text-melissa-ink">@melissaodonnell.beauty</div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-melissa-bg2 p-8 md:p-12 border border-melissa-border space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Full name *</Label>
              <Input
                id="name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your full name"
                className="bg-melissa-bg border-melissa-border rounded-none h-12 focus-visible:ring-melissa-rose"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
                className="bg-melissa-bg border-melissa-border rounded-none h-12 focus-visible:ring-melissa-rose"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Phone *</Label>
              <Input
                id="phone"
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+1 (555) 555-0000"
                className="bg-melissa-bg border-melissa-border rounded-none h-12 focus-visible:ring-melissa-rose"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Service *</Label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger
                  data-testid="contact-select-service"
                  className="bg-melissa-bg border-melissa-border rounded-none h-12 focus:ring-melissa-rose"
                >
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem
                      key={s.id}
                      value={s.name}
                      data-testid={`contact-service-option-${s.id}`}
                    >
                      {s.name} — ${s.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Preferred date *</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="contact-datepicker-button"
                    className="w-full h-12 px-3 bg-melissa-bg border border-melissa-border rounded-none flex items-center justify-between text-left text-sm hover:border-melissa-ink transition-colors"
                  >
                    <span className={date ? "text-melissa-ink" : "text-melissa-inkSoft"}>
                      {date ? format(date, "PPP") : "Pick a date"}
                    </span>
                    <CalendarIcon size={16} className="text-melissa-inkSoft" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Preferred time *</Label>
              <Select value={form.time} onValueChange={(v) => update("time", v)}>
                <SelectTrigger
                  data-testid="contact-select-time"
                  className="bg-melissa-bg border-melissa-border rounded-none h-12 focus:ring-melissa-rose"
                >
                  <SelectValue placeholder="Pick a time" />
                </SelectTrigger>
                <SelectContent>
                  {TIMES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`contact-time-option-${t.replace(/[^a-zA-Z0-9]/g, '-')}`}
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location" className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Event / location</Label>
            <Input
              id="location"
              data-testid="contact-input-location"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="Optional — venue, neighborhood, or city"
              className="bg-melissa-bg border-melissa-border rounded-none h-12 focus-visible:ring-melissa-rose"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="referral" className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">
              Referral code <span className="text-melissa-inkSoft/60 normal-case tracking-normal">(optional — saves you $25)</span>
            </Label>
            <Input
              id="referral"
              data-testid="contact-input-referral"
              value={form.referred_by}
              onChange={(e) => update("referred_by", e.target.value.toUpperCase())}
              onBlur={validateReferral}
              placeholder="MEL-XXXXXX"
              className={`bg-melissa-bg rounded-none h-12 focus-visible:ring-melissa-rose font-mono tracking-wider ${
                referral.status === "valid"
                  ? "border-emerald-500"
                  : referral.status === "invalid"
                  ? "border-red-400"
                  : "border-melissa-border"
              }`}
            />
            {referral.message && (
              <p
                data-testid="contact-referral-message"
                className={`text-xs ${
                  referral.status === "valid" ? "text-emerald-700" :
                  referral.status === "checking" ? "text-melissa-inkSoft" : "text-red-600"
                }`}
              >
                {referral.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Tell me more</Label>
            <Textarea
              id="message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Anything you'd like me to know — your vision, attire, size of party, sensitivities..."
              className="bg-melissa-bg border-melissa-border rounded-none min-h-[140px] focus-visible:ring-melissa-rose"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-melissa-ink text-melissa-bg text-xs uppercase tracking-overline hover:bg-melissa-rose transition-colors duration-300 disabled:opacity-60"
          >
            {submitting
              ? "Sending…"
              : referral.status === "valid"
              ? `Reserve with $${(50 - referral.discount).toFixed(0)} Deposit (was $50)`
              : "Reserve with $50 Deposit"}
            <Send size={14} className="transition-transform group-hover:translate-x-1" />
          </button>
          <p className="text-[11px] text-melissa-inkSoft mt-2 font-light">
            A refundable $50 deposit secures your date. Cancel up to 7 days prior for a full refund.
          </p>
        </form>
      </div>
    </section>
  );
}
